import React, { useState } from 'react';
import { products as initialProducts } from '../data/products';
import { Plus, Edit, Trash2, Save, X, Search, Filter, BarChart3, Package, TrendingUp, AlertTriangle } from 'lucide-react';

const AdminPanel = () => {
  const [products, setProducts] = useState(initialProducts);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [currentView, setCurrentView] = useState('products'); // 'products', 'dashboard', 'analytics'

  const [formData, setFormData] = useState({
    name: '',
    category: 'Bœuf',
    price: '',
    unit: 'kg',
    description: '',
    image: '',
    stock: '',
    featured: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAddProduct = (e) => {
    e.preventDefault();
    const newProduct = {
      id: Math.max(...products.map(p => p.id)) + 1,
      ...formData,
      price: parseFloat(formData.price),
      stock: parseInt(formData.stock)
    };
    setProducts([...products, newProduct]);
    setFormData({
      name: '',
      category: 'Bœuf',
      price: '',
      unit: 'kg',
      description: '',
      image: '',
      stock: '',
      featured: false
    });
    setShowAddForm(false);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product.id);
    setFormData({
      name: product.name,
      category: product.category,
      price: product.price.toString(),
      unit: product.unit,
      description: product.description,
      image: product.image,
      stock: product.stock.toString(),
      featured: product.featured
    });
    setIsEditing(true);
  };

  const handleUpdateProduct = (e) => {
    e.preventDefault();
    setProducts(products.map(product => 
      product.id === editingProduct 
        ? {
            ...product,
            ...formData,
            price: parseFloat(formData.price),
            stock: parseInt(formData.stock)
          }
        : product
    ));
    setIsEditing(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      category: 'Bœuf',
      price: '',
      unit: 'kg',
      description: '',
      image: '',
      stock: '',
      featured: false
    });
  };

  const handleDeleteProduct = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      setProducts(products.filter(product => product.id !== id));
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditingProduct(null);
    setShowAddForm(false);
    setFormData({
      name: '',
      category: 'Bœuf',
      price: '',
      unit: 'kg',
      description: '',
      image: '',
      stock: '',
      featured: false
    });
  };

  // Fonctions utilitaires pour les statistiques
  const getStats = () => {
    const totalProducts = products.length;
    const lowStockProducts = products.filter(p => p.stock < 5).length;
    const featuredProducts = products.filter(p => p.featured).length;
    const totalValue = products.reduce((sum, p) => sum + (p.price * p.stock), 0);
    const categories = [...new Set(products.map(p => p.category))];
    
    return {
      totalProducts,
      lowStockProducts,
      featuredProducts,
      totalValue,
      categories: categories.length
    };
  };

  // Filtrage des produits
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const stats = getStats();

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>Administration La Ferme À L'Ancienne</h1>
        <div className="admin-nav">
          <button 
            className={`nav-btn ${currentView === 'dashboard' ? 'active' : ''}`}
            onClick={() => setCurrentView('dashboard')}
          >
            <BarChart3 size={20} />
            Tableau de Bord
          </button>
          <button 
            className={`nav-btn ${currentView === 'products' ? 'active' : ''}`}
            onClick={() => setCurrentView('products')}
          >
            <Package size={20} />
            Produits
          </button>
        </div>
      </div>

      {/* Dashboard View */}
      {currentView === 'dashboard' && (
        <div className="dashboard">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <Package size={24} />
              </div>
              <div className="stat-content">
                <h3>{stats.totalProducts}</h3>
                <p>Produits Total</p>
              </div>
            </div>
            
            <div className="stat-card warning">
              <div className="stat-icon">
                <AlertTriangle size={24} />
              </div>
              <div className="stat-content">
                <h3>{stats.lowStockProducts}</h3>
                <p>Stock Faible</p>
              </div>
            </div>
            
            <div className="stat-card success">
              <div className="stat-icon">
                <TrendingUp size={24} />
              </div>
              <div className="stat-content">
                <h3>{stats.featuredProducts}</h3>
                <p>Coups de Cœur</p>
              </div>
            </div>
            
            <div className="stat-card primary">
              <div className="stat-icon">
                <BarChart3 size={24} />
              </div>
              <div className="stat-content">
                <h3>{stats.totalValue.toFixed(0)}€</h3>
                <p>Valeur Stock</p>
              </div>
            </div>
          </div>

          <div className="dashboard-content">
            <div className="dashboard-section">
              <h3>Produits en Stock Faible</h3>
              <div className="low-stock-list">
                {products.filter(p => p.stock < 5).map(product => (
                  <div key={product.id} className="low-stock-item">
                    <img src={product.image} alt={product.name} className="product-mini-thumb" />
                    <div className="product-info">
                      <h4>{product.name}</h4>
                      <p>Stock: {product.stock} {product.unit}</p>
                    </div>
                    <button 
                      className="quick-edit-btn"
                      onClick={() => handleEditProduct(product)}
                    >
                      <Edit size={16} />
                    </button>
                  </div>
                ))}
                {products.filter(p => p.stock < 5).length === 0 && (
                  <p className="no-items">Aucun produit en stock faible</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Products View */}
      {currentView === 'products' && (
        <div className="products-management">
          <div className="products-header">
            <div className="search-filter-bar">
              <div className="search-box">
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="filter-box">
                <Filter size={20} />
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="all">Toutes catégories</option>
                  <option value="Bœuf">Bœuf</option>
                  <option value="Porc">Porc</option>
                  <option value="Agneau">Agneau</option>
                  <option value="Veau">Veau</option>
                  <option value="Volaille">Volaille</option>
                  <option value="Charcuterie">Charcuterie</option>
                </select>
              </div>
            </div>
            
            <button 
              className="add-product-btn"
              onClick={() => setShowAddForm(true)}
            >
              <Plus size={20} />
              Ajouter un Produit
            </button>
          </div>
          
          {/* Products Table */}
          <div className="products-table">
            <table>
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Nom</th>
                  <th>Catégorie</th>
                  <th>Prix</th>
                  <th>Stock</th>
                  <th>Coup de Cœur</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(product => (
                  <tr key={product.id}>
                    <td>
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="product-thumbnail"
                      />
                    </td>
                    <td>{product.name}</td>
                    <td>{product.category}</td>
                    <td>{product.price}€/{product.unit}</td>
                    <td className={product.stock < 5 ? 'low-stock' : ''}>
                      {product.stock} {product.unit}
                    </td>
                    <td>{product.featured ? '⭐' : ''}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditProduct(product)}
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || isEditing) && (
        <div className="product-form-overlay">
          <div className="product-form">
            <div className="form-header">
              <h2>{isEditing ? 'Modifier le Produit' : 'Ajouter un Produit'}</h2>
              <button onClick={cancelEdit} className="close-btn">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={isEditing ? handleUpdateProduct : handleAddProduct}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Nom du produit *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Catégorie *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Bœuf">Bœuf</option>
                    <option value="Porc">Porc</option>
                    <option value="Agneau">Agneau</option>
                    <option value="Veau">Veau</option>
                    <option value="Volaille">Volaille</option>
                    <option value="Charcuterie">Charcuterie</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Prix *</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Unité</label>
                  <select
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                  >
                    <option value="kg">kg</option>
                    <option value="pièce">pièce</option>
                    <option value="barquette">barquette</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Stock *</label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>URL de l'image</label>
                  <input
                    type="url"
                    name="image"
                    value={formData.image}
                    onChange={handleInputChange}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                ></textarea>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                  />
                  Produit coup de cœur
                </label>
              </div>

              <div className="form-actions">
                <button type="button" onClick={cancelEdit} className="cancel-btn">
                  Annuler
                </button>
                <button type="submit" className="save-btn">
                  <Save size={20} />
                  {isEditing ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;